import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { NetEligibilityNotice } from './NetEligibilityNotice';
import { parseOptionalHandicap, handicapLabel, hasHandicap, eventHandicap } from '../utils/handicap';
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { setActiveRoundForYardage, clearActiveRound } from "./RonRoHelpButton";
import { 
  Flag,
  Trophy,
  Calendar as CalendarIcon,
  Trash2,
  ChevronDown,
  Camera,
  X,
  Check,
  Users,
  Settings,
  ArrowLeft,
  Play,
  UserPlus,
  UserCircle,
  User,
  DollarSign,
  Sparkles,
  PartyPopper,
  MapPin,
  Edit,
  Medal,
  RefreshCw,
  ExternalLink,
  MoreVertical,
  Link2,
  Search
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { format } from "date-fns";
import ScoreEntryForm from "./ScoreEntryForm";
import { AddPlayerModal } from "../App";
import { CourseLookupModal, CourseParDisplay } from "./score-entry";
import UserBubble from "./UserBubble";
import { JoinTournamentButton, TournamentPrizeInfo } from "./JoinTournamentButton";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Hook to detect current theme
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    return !document.documentElement.classList.contains('theme-light');
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(!document.documentElement.classList.contains('theme-light'));
    };
    
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  return isDark;
};

// Helper function to get score styling based on score vs par
const getScoreStyle = (score, holePar) => {
  if (!score || !holePar) return { shape: 'default', scoreType: 'default' };
  
  const diff = score - holePar;
  
  if (diff <= -2) {
    return { shape: 'circle-double', scoreType: 'eagle' };
  } else if (diff === -1) {
    return { shape: 'circle', scoreType: 'birdie' };
  } else if (diff === 0) {
    return { shape: 'square', scoreType: 'par' };
  } else if (diff === 1) {
    return { shape: 'default', scoreType: 'bogey' };
  } else {
    return { shape: 'default', scoreType: 'double' };
  }
};

const StrokePlayEventDetail = ({ eventId, onBack, user }) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('players');
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [playerScores, setPlayerScores] = useState({});
  const [submittingPlayer, setSubmittingPlayer] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  
  // Theme detection
  const isDark = useTheme();
  
  // Edit player handicap state
  const [editingHandicapPlayer, setEditingHandicapPlayer] = useState(null);
  const [newHandicapValue, setNewHandicapValue] = useState('');
  
  // Course lookup modal
  const [showCourseLookup, setShowCourseLookup] = useState(false);
  
  // Expanded player scores in results
  const [expandedResultsPlayer, setExpandedResultsPlayer] = useState(null);
  
  // Link player to user modal state
  const [showLinkPlayerModal, setShowLinkPlayerModal] = useState(false);
  const [linkingPlayerId, setLinkingPlayerId] = useState(null);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  
  // Sync scores to history state
  const [syncingScores, setSyncingScores] = useState(false);
  const [scoresSynced, setScoresSynced] = useState(false);
  
  // Use refs to track current state for polling
  const isEditingRef = useRef(false);
  const playerScoresRef = useRef({});
  const openedEvent = useRef(null);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/stroke-play/events/${eventId}`);
      setEvent(response.data);
      if (openedEvent.current !== eventId) {
        openedEvent.current = eventId;
        setActiveTab(response.data.status === 'active' ? 'scorecards' : response.data.status === 'completed' ? 'results' : 'players');
      }
      
      // Set active round for RonRo yardage mode when game is in progress
      if (response.data.status === 'active' && response.data.course_id) {
        setActiveRoundForYardage({
          course_id: response.data.course_id,
          course_name: response.data.course_name,
          game_type: 'stroke_play',
          event_id: eventId,
          current_hole: 1
        });
      } else if (response.data.status === 'completed') {
        clearActiveRound();
      }
      
      // Initialize scores for players from submitted scorecards
      const initialScores = {};
      const scorecards = response.data.scorecards || [];
      for (const sc of scorecards) {
        if (sc.hole_scores && sc.hole_scores.length > 0) {
          initialScores[sc.player_id] = sc.hole_scores.map(s => s?.toString() || '');
        }
      }
      
      // Load draft scores for players without submitted scorecards
      for (const player of response.data.players || []) {
        if (!initialScores[player.player_id]) {
          try {
            const draftRes = await axios.get(`${API}/stroke-play/events/${eventId}/scorecards/draft/${player.player_id}`);
            if (draftRes.data?.hole_scores) {
              initialScores[player.player_id] = draftRes.data.hole_scores.map(s => s?.toString() || '');
            }
          } catch (e) {
            // No draft exists - that's fine
          }
        }
      }
      
      if (!isEditingRef.current) {
        setPlayerScores(initialScores);
        playerScoresRef.current = initialScores;
      }
    } catch (err) {
      toast.error("Failed to load event");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
    
    // Polling for updates
    const interval = setInterval(() => {
      if (!isEditingRef.current) {
        fetchEvent();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [eventId]);

  // Track editing state
  const handleScoreChange = (playerId, scores) => {
    isEditingRef.current = true;
    setPlayerScores(prev => {
      const updated = { ...prev, [playerId]: scores };
      playerScoresRef.current = updated;
      return updated;
    });
  };

  // Player map for quick lookup
  const playerMap = useMemo(() => {
    const map = {};
    (event?.players || []).forEach(p => { map[p.player_id] = p; });
    return map;
  }, [event?.players]);

  // Get scorecard for a player
  const getPlayerScorecard = (playerId) => {
    return (event?.scorecards || []).find(sc => sc.player_id === playerId);
  };

  const removePlayer = async (playerId) => {
    try {
      await axios.delete(`${API}/stroke-play/events/${eventId}/players/${playerId}`);
      toast.success("Player removed");
      fetchEvent();
    } catch (err) {
      toast.error("Failed to remove player");
    }
  };

  const updatePlayerHandicap = async (playerId, handicap) => {
    try {
      const hcpValue = parseOptionalHandicap(handicap);
      await axios.patch(`${API}/stroke-play/events/${eventId}/players/${playerId}`, {
        handicap: hcpValue
      });
      toast.success("Handicap updated");
      setEditingHandicapPlayer(null);
      setNewHandicapValue('');
      fetchEvent();
    } catch (err) {
      toast.error("Failed to update handicap");
    }
  };

  // Search for users to link to guest player
  const searchUsersForLink = async (query) => {
    if (query.length < 2) {
      setLinkSearchResults([]);
      return;
    }
    setLinkSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`);
      setLinkSearchResults(response.data.users || []);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLinkSearching(false);
    }
  };

  // Link a guest player to a Par Seeker user account
  const linkPlayerToUser = async (playerId, targetUser) => {
    try {
      await axios.patch(`${API}/stroke-play/events/${eventId}/players/${playerId}`, {
        user_id: targetUser.user_id
      });
      toast.success(`Linked ${targetUser.name} to this player! They can now view this game in their dashboard.`);
      setShowLinkPlayerModal(false);
      setLinkingPlayerId(null);
      setLinkSearchQuery('');
      setLinkSearchResults([]);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link player");
    }
  };

  // Sync scores from completed game to Recent Rounds/History
  const syncScoresToHistory = async () => {
    setSyncingScores(true);
    try {
      const response = await axios.post(`${API}/stroke-play/events/${eventId}/sync-scores`);
      if (response.data.scores_synced > 0) {
        toast.success(`Synced ${response.data.scores_synced} score(s) to your Recent Rounds!`);
        setScoresSynced(true);
      } else {
        toast.info("Scores already synced or no linked players with scorecards");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync scores");
    } finally {
      setSyncingScores(false);
    }
  };

  const startEvent = async () => {
    try {
      await axios.post(`${API}/stroke-play/events/${eventId}/start`);
      toast.success("Game started!");
      setActiveTab('scorecards');
      fetchEvent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start event");
    }
  };

  const submitScorecard = async (playerId, scores) => {
    setSubmittingPlayer(playerId);
    try {
      const holeScores = scores.map(s => s ? parseInt(s) : null);
      
      await axios.post(`${API}/stroke-play/events/${eventId}/scorecards`, {
        player_id: playerId,
        hole_scores: holeScores
      });
      toast.success("Scorecard submitted!");
      isEditingRef.current = false;
      
      // Fetch updated event and check if all scorecards are submitted
      const response = await axios.get(`${API}/stroke-play/events/${eventId}`);
      const updatedEvent = response.data;
      setEvent(updatedEvent);
      
      // Auto-calculate results when all players have submitted scorecards
      const allPlayers = updatedEvent.players || [];
      const submittedScorecards = updatedEvent.scorecards || [];
      
      if (allPlayers.length >= 2 && submittedScorecards.length === allPlayers.length && !updatedEvent.results) {
        toast.info("All scorecards in! Calculating results...");
        setTimeout(() => calculateResults(), 500);
      }
    } catch (err) {
      toast.error("Failed to submit scorecard");
    } finally {
      setSubmittingPlayer(null);
    }
  };

  const saveDraftScores = async (playerId, scores) => {
    try {
      const holeScores = scores.map(s => s ? parseInt(s) : null);
      await axios.put(`${API}/stroke-play/events/${eventId}/scorecards/draft`, {
        player_id: playerId,
        hole_scores: holeScores
      });
      isEditingRef.current = false;
    } catch (err) {
      // Silent fail for draft saves
    }
  };

  const calculateResults = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/stroke-play/events/${eventId}/calculate`);
      
      // Show winner celebration
      const standings = response.data.gross_standings || [];
      if (standings.length > 0) {
        const winner = standings[0];
        setWinnerData({
          name: winner.player_name,
          score: winner.gross_score,
          netScore: winner.net_score,
          vspar: winner.score_vs_par,
          payout: response.data.payouts?.[winner.player_id] || 0
        });
        setShowWinnerModal(true);
      }
      
      toast.success("Results calculated!");
      fetchEvent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to calculate results");
    } finally {
      setCalculating(false);
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm("Are you sure you want to delete this game? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/stroke-play/events/${eventId}`);
      toast.success("Game deleted");
      onBack();
    } catch (err) {
      toast.error("Failed to delete event");
    }
  };

  const handleCourseSelect = async (course) => {
    try {
      await axios.patch(`${API}/stroke-play/events/${eventId}`, {
        course_name: course.name,
        course_id: course.course_id,
        hole_pars: course.hole_pars,
        hole_handicaps: course.hole_handicaps
      });
      toast.success("Course updated");
      setShowCourseLookup(false);
      fetchEvent();
    } catch (err) {
      toast.error("Failed to update course");
    }
  };

  // Refresh course data from GolfCourseAPI
  const [refreshingCourse, setRefreshingCourse] = useState(false);
  
  const refreshCourseData = async () => {
    if (!event?.course_name) {
      toast.error('No course selected');
      return;
    }
    
    setRefreshingCourse(true);
    try {
      const searchResponse = await axios.post(`${API}/voice/lookup-course`, {
        course_name: event.course_name
      });
      
      if (searchResponse.data.success) {
        if (searchResponse.data.multiple_results) {
          toast.info('Multiple courses found - please select the correct one');
          setShowCourseLookup(true);
        } else if (searchResponse.data.course) {
          const courseData = searchResponse.data.course;
          await axios.patch(`${API}/stroke-play/events/${eventId}`, {
            course_name: courseData.name,
            hole_pars: courseData.hole_pars || [],
            hole_handicaps: courseData.hole_handicaps || []
          });
          toast.success(`Course data refreshed from ${searchResponse.data.source === 'golfcourseapi' ? 'GolfCourseAPI' : 'database'}`);
          fetchEvent();
        }
      } else {
        toast.error(searchResponse.data.message || 'Course not found in database');
      }
    } catch (error) {
      toast.error('Failed to refresh course data');
    } finally {
      setRefreshingCourse(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--ps-text-secondary)]">Event not found</p>
        <Button variant="link" onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const holePars = event.hole_pars || [];
  const numHoles = event.holes || 18;
  const totalPar = holePars.length > 0 ? holePars.slice(0, numHoles).reduce((a, b) => a + b, 0) : (numHoles === 9 ? 36 : 72);

  // Helper function to get actual hole number based on starting nine
  const getActualHoleNumber = (idx) => {
    if (event.starting_nine === 'back' && numHoles === 9) {
      return idx + 10;
    }
    return idx + 1;
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="stroke-play-event-detail">
      <NetEligibilityNotice event={event} />
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Button variant="ghost" onClick={onBack} className="p-2" data-testid="stroke-event-back" aria-label="Back to games">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-[var(--ps-text-primary)] truncate flex items-center gap-2">
            <Trophy className="w-5 h-5 text-blue-600 flex-shrink-0" />
            {event.name}
          </h1>
          <div className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)] flex-wrap">
            <CalendarIcon className="w-4 h-4" />
            <span>{event.date}</span>
            <span>•</span>
            <span>{event.holes} holes</span>
            {event.use_handicaps && (
              <>
                <span>•</span>
                <span className="text-blue-600 font-medium">Net scoring</span>
              </>
            )}
            {event.entry_fee > 0 && (
              <>
                <span>•</span>
                <span className="text-green-600 font-medium">${event.entry_fee} entry</span>
              </>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          event.status === 'active' ? 'bg-blue-100 text-blue-700' :
          event.status === 'completed' ? 'bg-green-100 text-green-700' :
          'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]'
        }`}>
          {event.status === 'active' ? 'In Progress' : event.status === 'completed' ? 'Completed' : 'Setup'}
        </span>
      </div>

      {/* Tournament Entry Fee - Join Button for Non-Players */}
      {event.entry_fee > 0 && !event.is_creator && event.status === 'setup' && 
       !(event.players || []).some(p => p.user_id === user?.user_id) && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <TournamentPrizeInfo 
              entryFee={event.entry_fee} 
              playerCount={(event.players || []).length}
              compact={true}
            />
            <JoinTournamentButton
              tournamentId={eventId}
              tournamentType="stroke_play"
              entryFee={event.entry_fee}
              tournamentName={event.name}
              onJoined={() => fetchEvent()}
              data-testid="join-stroke-play-btn"
            />
          </div>
        </div>
      )}

      {/* Game Info Badge - Shows holes being played for 9-hole games */}
      {numHoles === 9 && (
        <div className="mb-4 flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            event.starting_nine === 'back' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {event.starting_nine === 'back' ? 'Back 9 (Holes 10-18)' : 'Front 9 (Holes 1-9)'}
          </span>
        </div>
      )}

      {/* Course Par Display - Full display with handicaps like Skins */}
      {event.course_name && holePars.length > 0 && (
        <div className="mb-4">
          <CourseParDisplay
            selectedCourseData={{
              name: event.course_name,
              location: event.course_location,
              par: totalPar,
              hole_pars: holePars,
              hole_handicaps: event.hole_handicaps || []
            }}
            numberOfHoles={numHoles}
            startingNine={event.starting_nine || "front"}
            setStartingNine={() => {}}
            getActualHoleNumber={getActualHoleNumber}
          />
          {event.is_creator && event.status === 'setup' && (
            <div className="mt-2 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowCourseLookup(true)} className="text-blue-600">
                <Edit className="w-4 h-4 mr-1" />
                Change Course
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Simple Course Card - Fallback when no hole pars available */}
      {event.course_name && holePars.length === 0 && (
        <Card className="mb-4 border-blue-200 bg-blue-50/50">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-[var(--ps-text-primary)]">{event.course_name}</span>
                <span className="text-sm text-[var(--ps-text-muted)]">• Par {totalPar}</span>
              </div>
              {event.is_creator && event.status === 'setup' && (
                <Button variant="ghost" size="sm" onClick={() => setShowCourseLookup(true)} className="text-blue-600">
                  <Edit className="w-4 h-4 mr-1" />
                  Change
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Course Lookup Modal */}
      {showCourseLookup && (
        <CourseLookupModal
          isOpen={true}
          onClose={() => setShowCourseLookup(false)}
          onCourseSelect={handleCourseSelect}
          title="Change Course"
        />
      )}

      {/* Tabs - Large & Obvious */}
      <div className="mb-4">
        <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-1.5 grid grid-cols-4 gap-1">
          {[
            { id: 'players', label: 'Players', icon: Users },
            { id: 'scorecards', label: 'Scores', icon: Flag },
            { id: 'results', label: 'Results', icon: Trophy },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-2 rounded-lg text-sm font-bold transition-all flex flex-col items-center gap-1 ${
                activeTab === tab.id
                  ? 'bg-[var(--ps-bg-surface)] text-blue-700 shadow-sm'
                  : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Players Tab */}
      {activeTab === 'players' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Players ({(event.players || []).length})
              </CardTitle>
              {event.is_creator && event.status === 'setup' && (
                <Button onClick={() => setShowAddPlayerModal(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add Player
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {(event.players || []).length === 0 ? (
              <div className="text-center py-8 text-[var(--ps-text-secondary)]">
                <Users className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p>No players added yet</p>
                <p className="text-sm">Add at least 2 players to start the game</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(event.players || []).map((player) => {
                  const scorecard = getPlayerScorecard(player.player_id);
                  const isLinkedUser = !player.is_guest && player.user_id;
                  const isEditing = editingHandicapPlayer === player.player_id;
                  const canEdit = event.is_creator && event.status !== 'completed';
                  const hasHandicap = player.handicap !== null && player.handicap !== undefined;
                  
                  return (
                    <div key={player.player_id} className={`rounded-xl overflow-hidden border ${
                      isLinkedUser ? 'border-blue-200 bg-blue-50/50' : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]'
                    }`}>
                      {/* Main Player Info Row */}
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <UserBubble
                            userId={player.user_id}
                            picture={player.picture}
                            name={player.name}
                            bubbleColor={isLinkedUser ? 'blue' : 'green'}
                            size="md"
                            clickable={!!player.user_id}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-[var(--ps-text-primary)] truncate">{player.name}</p>
                              {isLinkedUser && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0">Linked</span>
                              )}
                              {player.is_guest && !isLinkedUser && (
                                <span className="text-xs bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)] px-1.5 py-0.5 rounded flex-shrink-0">Guest</span>
                              )}
                            </div>
                            {player.email && <p className="text-xs text-[var(--ps-text-muted)] truncate">{player.email}</p>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Handicap Badge - Always Visible */}
                          <button
                            onClick={() => {
                              if (canEdit) {
                                setEditingHandicapPlayer(isEditing ? null : player.player_id);
                                setNewHandicapValue(player.handicap?.toString() || '');
                              }
                            }}
                            disabled={!canEdit}
                            className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-all ${
                              hasHandicap 
                                ? 'bg-green-100 text-green-700 border border-green-200' 
                                : 'bg-amber-100 text-amber-700 border border-amber-200'
                            } ${canEdit ? 'cursor-pointer hover:shadow-md active:scale-95' : 'cursor-default'}`}
                            title={canEdit ? "Tap to edit handicap" : ""}
                          >
                            HCP {hasHandicap ? player.handicap : '—'}
                            {canEdit && <Edit className="w-3 h-3 opacity-60" />}
                          </button>
                          
                          {/* Remove Button - Only in Setup */}
                          {event.is_creator && event.status === 'setup' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {/* Link to Par Seeker - only for guests */}
                                {player.is_guest && !player.user_id && (
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setLinkingPlayerId(player.player_id);
                                      setShowLinkPlayerModal(true);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    <Link2 className="w-4 h-4 mr-2 text-blue-600" />
                                    Link to Par Seeker
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => removePlayer(player.player_id)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove Player
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          
                          {/* Score Badge - If submitted */}
                          {scorecard && (
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-medium">
                              {scorecard.total_score}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expandable Edit Section */}
                      {isEditing && canEdit && (
                        <div className="px-3 pb-3 pt-0 border-t border-[var(--ps-border)]">
                          <div className="bg-[var(--ps-bg-surface)] rounded-lg p-3 shadow-inner space-y-3">
                            <p className="text-xs font-medium text-[var(--ps-text-secondary)]">Edit Handicap for {player.name}</p>
                            
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.1"
                                min="-5"
                                max="54"
                                className="flex-1 h-10"
                                placeholder="Enter handicap (e.g., 12.5)"
                                value={newHandicapValue}
                                onChange={(e) => setNewHandicapValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') updatePlayerHandicap(player.player_id, newHandicapValue);
                                  if (e.key === 'Escape') { setEditingHandicapPlayer(null); setNewHandicapValue(''); }
                                }}
                                autoFocus
                              />
                              <Button 
                                size="sm" 
                                className="h-10 bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => updatePlayerHandicap(player.player_id, newHandicapValue)}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Save
                              </Button>
                            </div>
                            
                            {/* Reload from Profile Button - For linked users */}
                            {isLinkedUser && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={async () => {
                                  try {
                                    // Use the dedicated handicap endpoint for real-time data
                                    const res = await axios.get(`${API}/users/${player.user_id}/handicap`);
                                    const data = res.data;
                                    
                                    if (data.handicap !== null && data.handicap !== undefined) {
                                      await updatePlayerHandicap(player.player_id, data.handicap.toString());
                                      const source = data.handicap_source === 'manual' ? 'manual setting' : 'calculated from scores';
                                      toast.success(`Loaded handicap ${data.handicap} (${source})`);
                                    } else if (data.rounds_needed_for_handicap > 0) {
                                      toast.info(`Player needs ${data.rounds_needed_for_handicap} more round(s) for calculated handicap`);
                                    } else {
                                      toast.info("No handicap found - player can set one in their profile");
                                    }
                                  } catch (err) {
                                    if (err.response?.status === 404) {
                                      toast.error("Player profile not found");
                                    } else {
                                      toast.error("Could not load handicap from profile");
                                    }
                                  }
                                }}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reload from Player's Profile
                              </Button>
                            )}
                            
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1 text-[var(--ps-text-secondary)]"
                                onClick={() => { setEditingHandicapPlayer(null); setNewHandicapValue(''); }}
                              >
                                Cancel
                              </Button>
                              {hasHandicap && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex-1 text-red-500"
                                  onClick={() => updatePlayerHandicap(player.player_id, '')}
                                >
                                  Clear Handicap
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Warning for missing handicap */}
                      {!hasHandicap && !isEditing && event.use_handicaps && (
                        <div className="px-3 pb-2">
                          <p className="text-xs text-amber-600 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                            Handicap needed for net scoring - tap HCP to set
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {event.is_creator && event.status === 'setup' && (event.players || []).length >= 2 && (
              <div className="mt-4 pt-4 border-t">
                <Button onClick={startEvent} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <Play className="w-4 h-4 mr-2" />
                  Start Stroke Play Game
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Player Modal */}
      {showAddPlayerModal && (
        <AddPlayerModal 
          eventId={eventId}
          eventType="stroke-play"
          onClose={() => setShowAddPlayerModal(false)}
          onAdded={() => {
            setShowAddPlayerModal(false);
            fetchEvent();
          }}
        />
      )}

      {/* Link Player to Par Seeker Account Modal */}
      <Dialog open={showLinkPlayerModal} onOpenChange={(open) => {
        if (!open) {
          setShowLinkPlayerModal(false);
          setLinkingPlayerId(null);
          setLinkSearchQuery('');
          setLinkSearchResults([]);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Link to Par Seeker Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[var(--ps-text-secondary)]">
              Search for a registered Par Seeker user to link this guest player. 
              Once linked, they'll see this game in their dashboard.
            </p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
              <Input
                placeholder="Search by name or email..."
                value={linkSearchQuery}
                onChange={(e) => {
                  setLinkSearchQuery(e.target.value);
                  searchUsersForLink(e.target.value);
                }}
                className="pl-10"
                data-testid="link-user-search"
              />
            </div>
            
            {linkSearching && (
              <div className="text-center py-4">
                <div className="spinner w-6 h-6 border-blue-600 border-t-transparent mx-auto" />
              </div>
            )}
            
            {!linkSearching && linkSearchResults.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {linkSearchResults.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => linkPlayerToUser(linkingPlayerId, u)}
                    className="w-full flex items-center gap-3 p-3 bg-[var(--ps-bg-elevated)] hover:bg-blue-50 rounded-lg transition-colors text-left"
                    data-testid={`link-user-${u.user_id}`}
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[var(--ps-text-primary)] truncate">{u.name}</p>
                      <p className="text-xs text-[var(--ps-text-muted)] truncate">{u.email}</p>
                    </div>
                    <Link2 className="w-4 h-4 text-blue-600" />
                  </button>
                ))}
              </div>
            )}
            
            {!linkSearching && linkSearchQuery.length >= 2 && linkSearchResults.length === 0 && (
              <div className="text-center py-4 text-[var(--ps-text-secondary)]">
                <p className="text-sm">No users found matching "{linkSearchQuery}"</p>
                <p className="text-xs mt-1">They may need to create a Par Seeker account first</p>
              </div>
            )}
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">Why link accounts?</p>
              <p className="text-xs text-blue-600 mt-1">
                Linked players can see this game in their own Par Seeker dashboard, 
                and their scores can contribute to their handicap calculation.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkPlayerModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scorecards Tab */}
      {activeTab === 'scorecards' && (
        <Card>
          <CardContent className="p-4">
            {event.status === 'setup' ? (
              <div className="text-center py-8">
                <Camera className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p className="text-[var(--ps-text-secondary)] mb-4">Start the game to submit scorecards</p>
                {event.is_creator && (event.players || []).length >= 2 && (
                  <Button onClick={startEvent} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Play className="w-4 h-4 mr-2" />
                    Start Stroke Play Game
                  </Button>
                )}
                {event.is_creator && (event.players || []).length < 2 && (
                  <p className="text-sm text-amber-500 mt-2">Add at least 2 players to start the game</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-semibold text-[var(--ps-text-primary)] flex items-center gap-2">
                  <Flag className="w-5 h-5 text-blue-600" />
                  Player Scores
                </h3>
                
                <div className="space-y-4">
                  {(event.players || []).map((player) => {
                    const scorecard = getPlayerScorecard(player.player_id);
                    const isComplete = !!scorecard;
                    const currentScores = playerScores[player.player_id] || [];
                    
                    return (
                      <div key={player.player_id} className={`rounded-xl border-2 overflow-hidden ${
                        isComplete ? 'border-green-300 bg-green-50/50' : 'border-blue-200 bg-[var(--ps-bg-surface)]'
                      }`}>
                        <div className={`p-3 flex items-center justify-between ${
                          isComplete ? 'bg-green-100' : 'bg-blue-50'
                        }`}>
                          <div className="flex items-center gap-2">
                            <UserBubble
                              userId={player.user_id}
                              picture={player.picture}
                              name={player.name}
                              bubbleColor={isComplete ? 'green' : 'blue'}
                              size="sm"
                              clickable={!!player.user_id}
                            />
                            <div>
                              <p className="font-semibold text-[var(--ps-text-primary)]">{player.name}</p>
                              <p className="text-xs text-[var(--ps-text-muted)]">
                                HCP: {handicapLabel(player.handicap)}
                              </p>
                            </div>
                          </div>
                          {isComplete ? (
                            <div className="flex items-center gap-2">
                              <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                {scorecard.total_score}
                              </span>
                              <Check className="w-5 h-5 text-green-600" />
                            </div>
                          ) : (
                            <span className="text-xs text-blue-600 font-medium">Enter scores below</span>
                          )}
                        </div>
                        
                        {/* Score Entry - Creator can edit all, participant can only edit their own */}
                        {!isComplete && (event.is_creator || (player.user_id && player.user_id === user?.user_id)) && (
                          <div className="p-3">
                            <ScoreEntryForm
                              courseId={event.course_id}
                              eventHoles={numHoles}
                              initialScores={currentScores}
                              onScoresChange={(scores) => handleScoreChange(player.player_id, scores)}
                              showHolesSelector={false}
                              startingNine={event.starting_nine || 'front'}
                              getActualHoleNumber={(idx) => event.starting_nine === 'back' && numHoles === 9 ? idx + 10 : idx + 1}
                              holePars={event.hole_pars || []}
                              holeYardages={event.hole_yardages || []}
                              showYardages={event.hole_yardages && event.hole_yardages.length > 0}
                              showVoiceOption={true}
                              showScanOption={true}
                              autoSave={true}
                              autoSaveEndpoint={`${API}/stroke-play/events/${eventId}/scorecards/draft`}
                              autoSavePayload={(scores) => ({
                                player_id: player.player_id,
                                hole_scores: scores.map(s => s === '' ? null : parseInt(s) || null)
                              })}
                              playerName={player.name}
                              submitButtonText="Finalize Score"
                              showTotalDisplay={true}
                              onSave={({ scores }) => submitScorecard(player.player_id, scores)}
                              disabled={submittingPlayer === player.player_id}
                            />
                          </div>
                        )}
                        
                        {/* View-only for other players when not creator */}
                        {!isComplete && !event.is_creator && player.user_id !== user?.user_id && (
                          <div className="p-4 text-center bg-stone-50 border-t">
                            <p className="text-sm text-[var(--ps-text-muted)]">
                              Waiting for {player.name} to submit their scorecard
                            </p>
                          </div>
                        )}
                        
                        {/* Show completed scores hole-by-hole like Skins */}
                        {isComplete && scorecard.hole_scores && scorecard.hole_scores.length > 0 && (
                          <div className="p-3 bg-[var(--ps-bg-surface)] border-t">
                            <div className="grid grid-cols-9 gap-1 text-center text-xs">
                              {(scorecard.hole_scores || []).slice(0, 9).map((score, idx) => {
                                const displayHole = event.starting_nine === 'back' && numHoles === 9 ? idx + 10 : idx + 1;
                                const holePar = holePars[event.starting_nine === 'back' && numHoles === 9 ? idx + 9 : idx] || 4;
                                const style = getScoreStyle(score, holePar);
                                
                                const getColors = () => {
                                  switch (style.scoreType) {
                                    case 'eagle': return { bg: '#fef9c3', color: '#854d0e', border: '#fde047' };
                                    case 'birdie': return { bg: '#dcfce7', color: '#166534', border: '#86efac' };
                                    case 'par': return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
                                    case 'bogey': return { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
                                    default: return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
                                  }
                                };
                                const colors = getColors();
                                const shapeClass = style.shape === 'circle' || style.shape === 'circle-double' ? 'rounded-full' : style.shape === 'square' ? 'rounded-none' : 'rounded';
                                
                                return (
                                  <div key={`hole-${idx + 1}`}>
                                    <div className="text-[var(--ps-text-muted)] mb-1 text-[10px]">{displayHole}</div>
                                    <div 
                                      className={`w-7 h-7 mx-auto ${shapeClass} flex items-center justify-center font-bold ${style.shape === 'circle-double' ? 'ring-2 ring-yellow-400' : ''}`}
                                      style={{ background: colors.bg, color: colors.color, border: `2px solid ${colors.border}` }}
                                    >
                                      {score}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {numHoles === 18 && scorecard.hole_scores?.length > 9 && (
                              <div className="grid grid-cols-9 gap-1 text-center text-xs mt-2">
                                {(scorecard.hole_scores || []).slice(9, 18).map((score, idx) => {
                                  const holePar = holePars[idx + 9] || 4;
                                  const style = getScoreStyle(score, holePar);
                                  
                                  const getColors = () => {
                                    switch (style.scoreType) {
                                      case 'eagle': return { bg: '#fef9c3', color: '#854d0e', border: '#fde047' };
                                      case 'birdie': return { bg: '#dcfce7', color: '#166534', border: '#86efac' };
                                      case 'par': return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
                                      case 'bogey': return { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
                                      default: return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
                                    }
                                  };
                                  const colors = getColors();
                                  const shapeClass = style.shape === 'circle' || style.shape === 'circle-double' ? 'rounded-full' : style.shape === 'square' ? 'rounded-none' : 'rounded';
                                  
                                  return (
                                    <div key={idx + 9}>
                                      <div className="text-[var(--ps-text-muted)] mb-1 text-[10px]">{idx + 10}</div>
                                      <div 
                                        className={`w-7 h-7 mx-auto ${shapeClass} flex items-center justify-center font-bold ${style.shape === 'circle-double' ? 'ring-2 ring-yellow-400' : ''}`}
                                        style={{ background: colors.bg, color: colors.color, border: `2px solid ${colors.border}` }}
                                      >
                                        {score}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Calculate Button */}
                {event.is_creator && (event.scorecards || []).length >= 2 && event.status === 'active' && (
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={calculateResults} 
                      disabled={calculating}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="calculate-results-btn"
                    >
                      {calculating ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Calculate Results'}
                    </Button>
                    <p className="text-xs text-[var(--ps-text-muted)] text-center mt-2">
                      {(event.scorecards || []).length} of {(event.players || []).length} scorecards submitted
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <Card>
          <CardContent className="p-4">
            {!event.results ? (
              <div className="text-center py-8 text-[var(--ps-text-secondary)]">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p>No results yet</p>
                <p className="text-sm">Results will appear after scorecards are submitted and calculated</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Winner Banner */}
                {event.results.gross_standings?.length > 0 && (
                  <button
                    onClick={() => {
                      const winner = event.results.gross_standings[0];
                      setWinnerData({
                        name: winner.player_name,
                        score: winner.gross_score,
                        netScore: winner.net_score,
                        vspar: winner.score_vs_par,
                        payout: event.results.payouts?.[winner.player_id] || 0
                      });
                      setShowWinnerModal(true);
                    }}
                    className="w-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] cursor-pointer relative overflow-hidden"
                    data-testid="winner-banner"
                  >
                    <div className="relative">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <Trophy className="w-6 h-6 text-white" />
                        <span className="text-white font-bold text-lg">WINNER</span>
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-2xl font-black text-white">
                        {event.results.gross_standings[0].player_name}
                      </p>
                      <p className="text-blue-100 font-semibold">
                        {event.results.gross_standings[0].gross_score} strokes 
                        ({event.results.gross_standings[0].score_vs_par >= 0 ? '+' : ''}{event.results.gross_standings[0].score_vs_par})
                        {event.results.payouts?.[event.results.gross_standings[0].player_id] > 0 && (
                          <span className="ml-2">• Wins ${event.results.payouts[event.results.gross_standings[0].player_id]}</span>
                        )}
                      </p>
                      <p className="text-xs text-blue-200 mt-1 animate-pulse">Tap to celebrate!</p>
                    </div>
                  </button>
                )}

                {/* Gross Standings */}
                <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] overflow-hidden">
                  <div className="bg-[var(--ps-bg-elevated)] px-4 py-2 font-semibold text-[var(--ps-text-secondary)] flex items-center gap-2">
                    <Medal className="w-5 h-5 text-blue-600" />
                    Gross Standings
                  </div>
                  <div className="divide-y divide-stone-100">
                    {(event.results.gross_standings || []).map((standing, idx) => {
                      const scorecard = getPlayerScorecard(standing.player_id);
                      const isExpanded = expandedResultsPlayer === standing.player_id;
                      
                      return (
                        <div key={standing.player_id}>
                          <button 
                            onClick={() => setExpandedResultsPlayer(isExpanded ? null : standing.player_id)}
                            className={`w-full flex items-center justify-between p-3 hover:bg-[var(--ps-bg-hover)] transition-colors ${idx === 0 ? 'bg-blue-50 hover:bg-blue-100' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                idx === 0 ? 'bg-blue-500 text-white' :
                                idx === 1 ? 'bg-[var(--ps-text-muted)] text-[var(--ps-text-primary)]' :
                                idx === 2 ? 'bg-amber-600 text-white' :
                                'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)]'
                              }`}>
                                {idx + 1}
                              </span>
                              <div className="text-left">
                                <p className="font-semibold text-[var(--ps-text-primary)]">{standing.player_name}</p>
                                <p data-testid={`stroke-gross-handicap-${standing.player_id}`} className="text-xs text-[var(--ps-text-muted)]">HCP: {handicapLabel(standing.handicap)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <p className="font-bold text-[var(--ps-text-primary)]">{standing.gross_score}</p>
                                <p className={`text-xs font-medium ${standing.score_vs_par <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {standing.score_vs_par >= 0 ? '+' : ''}{standing.score_vs_par}
                                </p>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-[var(--ps-text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </button>
                          
                          {/* Expanded hole-by-hole scores */}
                          {isExpanded && scorecard?.hole_scores && (
                            <div className="px-3 pb-3 bg-[var(--ps-bg-elevated)] border-t border-[var(--ps-border)]">
                              <p className="text-xs text-[var(--ps-text-muted)] font-medium mb-2 pt-2">Hole-by-Hole Scores</p>
                              <div className="grid grid-cols-9 gap-1 text-center text-xs">
                                {(scorecard.hole_scores || []).slice(0, 9).map((score, holeIdx) => {
                                  const displayHole = event.starting_nine === 'back' && numHoles === 9 ? holeIdx + 10 : holeIdx + 1;
                                  const holePar = holePars[event.starting_nine === 'back' && numHoles === 9 ? holeIdx + 9 : holeIdx] || 4;
                                  const style = getScoreStyle(score, holePar);
                                  
                                  const getColors = () => {
                                    switch (style.scoreType) {
                                      case 'eagle': return { bg: '#fef9c3', color: '#854d0e', border: '#fde047' };
                                      case 'birdie': return { bg: '#dcfce7', color: '#166534', border: '#86efac' };
                                      case 'par': return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
                                      case 'bogey': return { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
                                      default: return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
                                    }
                                  };
                                  const colors = getColors();
                                  const shapeClass = style.shape === 'circle' || style.shape === 'circle-double' ? 'rounded-full' : style.shape === 'square' ? 'rounded-none' : 'rounded';
                                  
                                  return (
                                    <div key={`hole-${holeIdx + 1}`}>
                                      <div className="text-[var(--ps-text-muted)] mb-1 text-[10px]">{displayHole}</div>
                                      <div 
                                        className={`w-7 h-7 mx-auto ${shapeClass} flex items-center justify-center font-bold ${style.shape === 'circle-double' ? 'ring-2 ring-yellow-400' : ''}`}
                                        style={{ background: colors.bg, color: colors.color, border: `2px solid ${colors.border}` }}
                                      >
                                        {score}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {numHoles === 18 && scorecard.hole_scores?.length > 9 && (
                                <div className="grid grid-cols-9 gap-1 text-center text-xs mt-2">
                                  {(scorecard.hole_scores || []).slice(9, 18).map((score, holeIdx) => {
                                    const holePar = holePars[holeIdx + 9] || 4;
                                    const style = getScoreStyle(score, holePar);
                                    
                                    const getColors = () => {
                                      switch (style.scoreType) {
                                        case 'eagle': return { bg: '#fef9c3', color: '#854d0e', border: '#fde047' };
                                        case 'birdie': return { bg: '#dcfce7', color: '#166534', border: '#86efac' };
                                        case 'par': return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
                                        case 'bogey': return { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
                                        default: return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
                                      }
                                    };
                                    const colors = getColors();
                                    const shapeClass = style.shape === 'circle' || style.shape === 'circle-double' ? 'rounded-full' : style.shape === 'square' ? 'rounded-none' : 'rounded';
                                    
                                    return (
                                      <div key={holeIdx + 9}>
                                        <div className="text-[var(--ps-text-muted)] mb-1 text-[10px]">{holeIdx + 10}</div>
                                        <div 
                                          className={`w-7 h-7 mx-auto ${shapeClass} flex items-center justify-center font-bold ${style.shape === 'circle-double' ? 'ring-2 ring-yellow-400' : ''}`}
                                          style={{ background: colors.bg, color: colors.color, border: `2px solid ${colors.border}` }}
                                        >
                                          {score}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Net Standings - Only if handicaps enabled */}
                {event.use_handicaps && event.results.net_standings?.length > 0 && (
                  <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <div className="bg-green-100 px-4 py-2 font-semibold text-green-700 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-green-600" />
                      Net Standings (Handicap Applied)
                    </div>
                    <div className="divide-y divide-stone-100">
                      {(event.results.net_standings || []).filter(s => s.net_eligible && hasHandicap(s.handicap) && s.net_score != null).map((standing, idx) => (
                        <div key={standing.player_id} className={`flex items-center justify-between p-3 ${idx === 0 ? 'bg-green-50' : ''}`}>
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              idx === 0 ? 'bg-green-500 text-white' :
                              idx === 1 ? 'bg-[var(--ps-text-muted)] text-[var(--ps-text-primary)]' :
                              idx === 2 ? 'bg-amber-600 text-white' :
                              'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)]'
                            }`}>
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-semibold text-[var(--ps-text-primary)]">{standing.player_name}</p>
                              <p className="text-xs text-[var(--ps-text-muted)]">
                                Gross: {standing.gross_score} • Strokes: -{standing.strokes_received}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-700">{standing.net_score}</p>
                            <p className="text-xs text-[var(--ps-text-muted)]">Net</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payouts */}
                {event.results.total_pot > 0 && Object.keys(event.results.payouts || {}).length > 0 && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                    <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      Payouts (Total Pot: ${event.results.total_pot})
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(event.results.payouts).map(([playerId, amount]) => {
                        const player = playerMap[playerId];
                        if (!amount || amount <= 0) return null;
                        return (
                          <div key={playerId} className="flex items-center justify-between bg-[var(--ps-bg-surface)]/70 rounded-lg p-2">
                            <span className="font-medium text-[var(--ps-text-primary)]">{player?.name || 'Unknown'}</span>
                            <span className="font-bold text-green-600">${amount}</span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Info about guest players */}
                    {event.players?.some(p => !p.user_id || p.is_guest) && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                        <p className="text-blue-800 font-medium">ℹ️ Playing with guests</p>
                        <p className="text-blue-600 mt-1">Your win/loss is tracked. Guest settlements show as "Guest" in your records.</p>
                      </div>
                    )}
                    <Link 
                      to="/settlements" 
                      className="mt-4 w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Go to My Settlements
                    </Link>
                  </div>
                )}

                {/* Sync Scores to Recent Rounds */}
                {event.status === 'completed' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-1">Add to Recent Rounds</h4>
                        <p className="text-sm text-blue-700 mb-3">
                          Sync these scores to your Recent Rounds so they count toward your handicap calculation.
                        </p>
                        <Button
                          onClick={syncScoresToHistory}
                          disabled={syncingScores || scoresSynced}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                          data-testid="sync-scores-btn"
                        >
                          {syncingScores ? (
                            <div className="spinner w-4 h-4 border-white border-t-transparent mr-2" />
                          ) : scoresSynced ? (
                            <Check className="w-4 h-4 mr-2" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          {scoresSynced ? 'Scores Synced!' : 'Sync to My Rounds'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card>
          <CardContent className="p-4 space-y-4">
            {/* Game Settings - Editable by Creator */}
            {event.is_creator && (
              <div className="p-4 bg-[var(--ps-bg-elevated)] rounded-lg">
                <h3 className="font-semibold text-[var(--ps-text-primary)] mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Game Settings
                </h3>
                
                {/* Holes Selection */}
                <div className="mb-4">
                  <Label className="text-sm text-[var(--ps-text-secondary)] mb-2 block">Number of Holes</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={event.holes === 9 ? "default" : "outline"}
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/stroke-play/events/${event.event_id}`, { holes: 9 });
                          toast.success("Changed to 9 holes");
                          fetchEvent();
                        } catch (err) {
                          toast.error("Failed to update holes");
                        }
                      }}
                      className={`flex-1 ${event.holes === 9 ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                    >
                      9 Holes
                    </Button>
                    <Button
                      variant={event.holes === 18 ? "default" : "outline"}
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/stroke-play/events/${event.event_id}`, { holes: 18 });
                          toast.success("Changed to 18 holes");
                          fetchEvent();
                        } catch (err) {
                          toast.error("Failed to update holes");
                        }
                      }}
                      className={`flex-1 ${event.holes === 18 ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                    >
                      18 Holes
                    </Button>
                  </div>
                </div>

                {/* Front/Back Selection - Only show for 9-hole games */}
                {event.holes === 9 && (
                  <div className="mb-4">
                    <Label className="text-sm text-[var(--ps-text-secondary)] mb-2 block">Which Nine?</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={event.starting_nine === 'front' ? "default" : "outline"}
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/stroke-play/events/${event.event_id}`, { starting_nine: 'front' });
                            toast.success("Changed to Front 9");
                            fetchEvent();
                          } catch (err) {
                            toast.error("Failed to update");
                          }
                        }}
                        className={`flex-1 ${event.starting_nine === 'front' ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                      >
                        Front 9 <span className="text-xs opacity-70 ml-1">(1-9)</span>
                      </Button>
                      <Button
                        variant={event.starting_nine === 'back' ? "default" : "outline"}
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/stroke-play/events/${event.event_id}`, { starting_nine: 'back' });
                            toast.success("Changed to Back 9");
                            fetchEvent();
                          } catch (err) {
                            toast.error("Failed to update");
                          }
                        }}
                        className={`flex-1 ${event.starting_nine === 'back' ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                      >
                        Back 9 <span className="text-xs opacity-70 ml-1">(10-18)</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Read-only Event Info */}
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Game Name</Label>
              <p className="text-[var(--ps-text-primary)]">{event.name}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Date</Label>
              <p className="text-[var(--ps-text-primary)]">{event.date}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Course</Label>
              <p className="text-[var(--ps-text-primary)]">{event.course_name || 'Not selected'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Holes</Label>
              <p className="text-[var(--ps-text-primary)]">{event.holes} holes {event.holes === 9 ? `(${event.starting_nine === 'back' ? 'Back 9' : 'Front 9'})` : ''}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Handicaps</Label>
              <p className="text-[var(--ps-text-primary)]">{event.use_handicaps ? 'Enabled (Net scoring)' : 'Disabled (Gross only)'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Entry Fee</Label>
              <p className="text-[var(--ps-text-primary)]">{event.entry_fee > 0 ? `$${event.entry_fee}` : 'None'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Payout Structure</Label>
              <p className="text-[var(--ps-text-primary)]">
                {event.payout_structure === 'winner' ? 'Winner Takes All' :
                 event.payout_structure === 'split' ? 'Split (60/30/10)' : 'No Payouts'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Created By</Label>
              <p className="text-[var(--ps-text-primary)]">{event.creator_name}</p>
            </div>
            
            {/* Course Data Section */}
            {event.is_creator && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Course Data
                </h3>
                {event.hole_pars && event.hole_pars.length > 0 && (
                  <div className="text-sm text-blue-800 mb-3">
                    <strong>Hole Pars:</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {event.hole_pars.slice(0, event.holes || 18).map((par, idx) => (
                        <span key={idx} className="w-6 h-6 flex items-center justify-center bg-[var(--ps-bg-surface)] rounded text-xs font-medium border border-blue-200">
                          {par}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    onClick={refreshCourseData} 
                    variant="outline" 
                    size="sm"
                    disabled={refreshingCourse}
                    className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshingCourse ? 'animate-spin' : ''}`} />
                    {refreshingCourse ? 'Refreshing...' : 'Refresh from API'}
                  </Button>
                  <Button 
                    onClick={() => setShowCourseLookup(true)} 
                    variant="outline" 
                    size="sm"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    Change Course
                  </Button>
                </div>
              </div>
            )}
            
            {event.is_creator && (
              <div className="pt-4 border-t">
                <Button variant="outline" onClick={deleteEvent} className="w-full text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Game
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Winner Celebration Modal */}
      <Dialog open={showWinnerModal} onOpenChange={setShowWinnerModal}>
        <DialogContent className="max-w-sm text-center">
          <div className="py-4">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center animate-bounce">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-black text-[var(--ps-text-primary)] mb-2">
              Congratulations!
            </h2>
            {winnerData && (
              <>
                <p className="text-3xl font-black text-blue-600 mb-2">
                  {winnerData.name}
                </p>
                <p className="text-lg text-[var(--ps-text-secondary)]">
                  {winnerData.score} strokes ({winnerData.vspar >= 0 ? '+' : ''}{winnerData.vspar})
                </p>
                {winnerData.payout > 0 && (
                  <p className="text-xl font-bold text-green-600 mt-2">
                    Wins ${winnerData.payout}!
                  </p>
                )}
              </>
            )}
            <div className="flex justify-center gap-2 mt-4">
              <PartyPopper className="w-8 h-8 text-amber-500 animate-pulse" />
              <Sparkles className="w-8 h-8 text-blue-500 animate-pulse" />
              <PartyPopper className="w-8 h-8 text-amber-500 animate-pulse" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowWinnerModal(false)} className="w-full bg-blue-600 hover:bg-blue-700">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrokePlayEventDetail;
